import React, { useEffect, useState } from 'react'
import { emailAPI, type RelayStatus } from '@hpanel/web-common'

/**
 * Relay info tab — shown on the tenant Email page. Read-only view of
 * the per-tenant outbound SMTP relay state: send count this hour vs
 * configured ceiling. The relay is automatically wired into every
 * tenant container at create time (msmtp + /usr/sbin/sendmail symlink),
 * so PHP `mail()`, Python `smtplib.SMTP('localhost')`, and shell `mail`
 * commands all route through the panel's shared Postfix without any
 * setup needed by the tenant.
 */
export const RelayInfoTab: React.FC = () => {
  const [status, setStatus] = useState<RelayStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const s = await emailAPI.relayStatus()
        if (!cancelled) setStatus(s)
      } catch (e: unknown) {
        if (!cancelled)
          setError(
            e instanceof Error ? e.message : 'Unable to reach server',
          )
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) return <div data-testid="relay-loading">Loading…</div>

  if (error)
    return (
      <div
        data-testid="relay-error"
        className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3 text-sm"
      >
        {error}
      </div>
    )

  if (!status) return null

  const usagePct = status.rate_per_hour
    ? Math.min(
        100,
        Math.round((status.sent_this_hour / status.rate_per_hour) * 100),
      )
    : 0

  return (
    <div data-testid="relay-info-tab" className="space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-3">Outbound relay</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {status.relay_host_hint}
        </p>
        <div className="flex gap-6 items-end">
          <div>
            <div className="text-xs text-gray-500">Sent this hour</div>
            <div
              className="text-2xl font-bold"
              data-testid="relay-sent-this-hour"
            >
              {status.sent_this_hour}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Ceiling per hour</div>
            <div
              className="text-2xl font-bold"
              data-testid="relay-rate-per-hour"
            >
              {status.rate_per_hour}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Status</div>
            <div
              className="text-2xl font-bold"
              data-testid="relay-enabled"
            >
              {status.enabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
        </div>
        {status.rate_per_hour > 0 && (
          <div className="mt-4">
            <div className="text-xs text-gray-500 mb-1">
              {usagePct}% of hourly ceiling used
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
              <div
                className={
                  usagePct >= 90
                    ? 'h-full bg-red-500'
                    : usagePct >= 70
                    ? 'h-full bg-yellow-500'
                    : 'h-full bg-green-500'
                }
                style={{ width: `${usagePct}%` }}
                data-testid="relay-usage-bar"
              />
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 text-sm">
        <h3 className="font-semibold mb-2">Sending mail from your container</h3>
        <p className="mb-2">
          Your container ships with a working <code>sendmail</code> command.
          Any script that calls <code>mail</code>, <code>php mail()</code>,
          or <code>python smtplib.SMTP('localhost')</code> will hand its
          message off to the shared mail server.
        </p>
        <pre className="bg-gray-900 text-green-200 font-mono text-xs p-2 rounded">
{`echo "Hello from $(hostname)" | mail -s "test" you@example.com`}
        </pre>
      </div>
    </div>
  )
}
